// MasterEmojiGeneratorModal — pt89T (Feb 20, 2026, Randy).
//
// Batch-of-5 Unicode emoji generator with silo-scoped persistent library.
// Different from the chore-creator single-emoji "Smart AI" button (that
// stays as-is, fast + simple). This one:
//   • Opens when the user taps any emoji SQUARE across AI Calendar
//     surfaces (chore emoji, event emoji, ping emoji, kid profile, reward).
//   • Has a Nudge input (biases every "Generate" batch — "welding shop",
//     "kids soccer", "morning routine", etc.).
//   • Has an "AI Generate" button that produces 5 fresh Unicode emojis.
//   • Persists every generated emoji into `silo.emoji_pool` so the
//     library grows over time. Reopening MEG on the same silo shows
//     every previously-added emoji, ready to pick again.
//   • Picking an emoji inserts it into the square that launched MEG,
//     via `onPick(emoji)`. When `preserveParent` is true (launched
//     from inside another modal like Create Chore), the MEG closes
//     only itself so the parent modal stays open. When false (launched
//     directly from an emoji square with no parent context), closing
//     the MEG returns to nothing else — it fully dismisses.
//
// Props:
//   • siloId (string, required)
//   • open (bool)
//   • onClose ()
//   • onPick (emoji: string) — required; called after user picks
//     an emoji. The parent decides whether to close its own modal.
//   • preserveParent (bool, default true) — cosmetic only; affects
//     the "Save & keep working" wording so the user sees the correct
//     expectation. The actual close-behavior is driven by the parent.
//   • title (string, optional) — override the modal title. Defaults
//     to "Master Emoji Generator".
import { useCallback, useEffect, useState } from "react";
import { X, Sparkle, Spinner, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function MasterEmojiGeneratorModal({
  siloId,
  open,
  onClose,
  onPick,
  preserveParent = true,
  title = "Master Emoji Generator",
}) {
  // pt89T-cap (Feb 20, 2026, Randy) — hard-cap the silo emoji library at
  // 50. Backend rejects the 51st save with HTTP 409. Frontend surfaces
  // the "N/50" counter + blocks Generate when full so the user knows
  // WHY save is failing before it fails. Matches the ping + chore caps.
  const EMOJI_POOL_CAP = 50;
  const [nudge, setNudge] = useState("");
  const [pool, setPool] = useState([]);          // { id, emoji, nudge, created_at }
  const [fresh, setFresh] = useState([]);        // just-generated batch, not yet saved
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const poolFull = pool.length >= EMOJI_POOL_CAP;

  const loadPool = useCallback(async () => {
    if (!siloId) return;
    setLoading(true);
    try {
      const r = await api.get(`/calendar/${siloId}/emoji-pool`);
      setPool(r.data?.emoji_pool || []);
    } catch { /* empty pool is a valid state */ }
    finally { setLoading(false); }
  }, [siloId]);

  useEffect(() => {
    if (open) {
      loadPool();
      setFresh([]);
    }
  }, [open, loadPool]);

  if (!open) return null;

  const generate = async () => {
    if (poolFull) {
      toast.error(`Emoji library is full (${EMOJI_POOL_CAP}/${EMOJI_POOL_CAP}). Delete an emoji you don't need to make room, then Generate again.`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/calendar/${siloId}/emoji-pool/generate`, {
        nudge: nudge.trim() || null,
        already_seen: pool.map((p) => p.emoji),
      });
      setFresh(r.data?.emojis || []);
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") {
        toast.error("Add your BYOK key or top up your voucher to run the AI.");
        return;
      }
      toast.error(e.response?.data?.detail || "Couldn't generate emojis this round.");
    } finally { setBusy(false); }
  };

  // Persist an emoji into the silo pool + immediately pick it. Idempotent
  // on the backend — reusing a fresh emoji that was already saved returns
  // the existing row without duplicating.
  const persistAndPick = async (emoji) => {
    if (!emoji) return;
    try {
      const r = await api.post(`/calendar/${siloId}/emoji-pool`, {
        emoji,
        nudge: nudge.trim() || null,
      });
      const entry = r.data?.entry;
      if (entry && !pool.find((p) => p.id === entry.id)) {
        setPool((cur) => [entry, ...cur]);
      }
      // Remove from the fresh strip so it doesn't sit as a "generated"
      // card AND a "saved" card at the same time.
      setFresh((cur) => cur.filter((e) => e !== emoji));
    } catch {
      // Save failed → still let the user pick locally so the flow isn't
      // blocked. The library refresh next open will re-hydrate anyway.
    }
    onPick && onPick(emoji);
    onClose && onClose();
  };

  const removeFromPool = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm(`Remove ${entry.emoji} from this silo's emoji library?`)) return;
    try {
      await api.delete(`/calendar/${siloId}/emoji-pool/${entry.id}`);
      setPool((cur) => cur.filter((p) => p.id !== entry.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove."); }
  };

  return (
    <div className="fixed inset-0 z-[260] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose} data-testid="master-emoji-generator-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFCF8] rounded-2xl w-full max-w-lg max-h-[92dvh] flex flex-col shadow-2xl border hairline">
        <div className="px-5 py-3 border-b hairline flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkle size={18} weight="duotone" className="text-[#C19A4A]" />
            <h2 className="font-serif-display text-lg text-[#1C1B1A] truncate">{title}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]"
            data-testid="master-emoji-close" aria-label="Close">
            <X size={17} />
          </button>
        </div>

        {/* Nudge + Generate row */}
        <div className="px-5 pt-4 pb-3 border-b hairline bg-[#FBFAF6]">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1.5">
              <Sparkle size={11} weight="duotone" className="text-[#C19A4A]" /> Nudge
              <span className="normal-case tracking-normal text-[#8A8A88] italic ml-auto">optional — biases every batch</span>
            </span>
            <div className="mt-1 flex gap-2">
              <input type="text" value={nudge}
                onChange={(e) => setNudge(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !busy) { e.preventDefault(); generate(); } }}
                data-testid="master-emoji-nudge-input"
                placeholder="e.g. welding shop, kids at soccer, morning routine"
                maxLength={120}
                className="flex-1 px-3 py-2 border hairline rounded-md bg-white text-sm focus:outline-none focus:border-[#4A5D4E]" />
              <button onClick={generate} disabled={busy || poolFull}
                data-testid="master-emoji-generate-btn"
                title={poolFull ? `Library full (${EMOJI_POOL_CAP}/${EMOJI_POOL_CAP}). Delete an emoji to make room.` : "Generate 5 fresh Unicode emojis"}
                className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
                {busy ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="duotone" />} Generate
              </button>
            </div>
            <span className="mt-1.5 block text-[10px] text-[#6B6A68] italic">
              5 fresh Unicode emojis per tap. Everything you pick is saved to this silo&rsquo;s emoji library forever.
            </span>
          </label>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Fresh batch — pulled from the last "Generate" tap */}
          {fresh.length > 0 && (
            <div data-testid="master-emoji-fresh-section">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2">Just generated</div>
              <div className="grid grid-cols-5 gap-2">
                {fresh.map((e) => (
                  <button key={e} onClick={() => persistAndPick(e)}
                    data-testid={`master-emoji-fresh-${e}`}
                    title="Pick and save to library"
                    className="aspect-square rounded-md border hairline bg-white hover:bg-[#F8F1E2] hover:border-[#C19A4A] flex items-center justify-center text-3xl transition-colors">
                    {e}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Saved library — the silo's persistent emoji pool */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2 flex items-center justify-between">
              <span data-testid="master-emoji-pool-count">Silo library ({pool.length}/{EMOJI_POOL_CAP})</span>
              {poolFull ? (
                <span className="normal-case tracking-normal text-[#C16E5A] italic" data-testid="master-emoji-pool-full">
                  Library full — delete some to add more
                </span>
              ) : preserveParent ? (
                <span className="normal-case tracking-normal text-[#8A8A88] italic">Pick returns to the pane</span>
              ) : (
                <span className="normal-case tracking-normal text-[#8A8A88] italic">Pick fills the square and closes</span>
              )}
            </div>
            {loading ? (
              <div className="text-xs text-[#6B6A68] italic flex items-center gap-1.5">
                <Spinner size={12} className="animate-spin" /> Loading library…
              </div>
            ) : pool.length === 0 ? (
              <div className="text-xs text-[#6B6A68] italic py-4 text-center">
                No emojis yet — tap <strong>Generate</strong> above (with or without a nudge) to grow this silo&rsquo;s emoji library.
              </div>
            ) : (
              <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                {pool.map((p) => (
                  <div key={p.id} className="relative group">
                    <button onClick={() => persistAndPick(p.emoji)}
                      data-testid={`master-emoji-pool-${p.id}`}
                      title={p.nudge ? `Pick — from nudge: ${p.nudge}` : "Pick this emoji"}
                      className="aspect-square w-full rounded-md border hairline bg-white hover:bg-[#F0F1EE] hover:border-[#4A5D4E] flex items-center justify-center text-2xl transition-colors">
                      {p.emoji}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); removeFromPool(p); }}
                      data-testid={`master-emoji-remove-${p.id}`}
                      title="Remove from library"
                      className="absolute -top-1 -right-1 p-0.5 rounded-full bg-white border hairline text-[#B5B3AE] hover:text-[#C16E5A] opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash size={11} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t hairline flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="master-emoji-cancel"
            className="text-xs px-3 py-2 rounded text-[#6B6A68] hover:bg-[#F0F1EE]">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
